import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useDashboardFiltersStore,
  hasActiveFilters,
  type DashboardFilters,
} from '@/store/dashboardFiltersStore';
import type { RejectionCategory } from '../api/dashboard.schemas';

/**
 * Renders one chip per active filter in `useDashboardFiltersStore`,
 * plus a "Clear all" button when ≥1 chip is shown. Hidden entirely
 * when no filters are active so the dashboard layout stays unchanged.
 *
 * Chip labels are localised; the underlying value (which is what the
 * server sees) is appended in parens for `device`/`browser`/`country`
 * where the raw value IS the user-facing label. Categories /
 * statuses get translated friendly names.
 */
export function FilterChips() {
  const { t } = useTranslation();
  const filters = useDashboardFiltersStore((s) => s.filters);
  const clearFilter = useDashboardFiltersStore((s) => s.clearFilter);
  const clearAll = useDashboardFiltersStore((s) => s.clearAll);

  // i18next's typed `TFunction` is a 9-overload union — narrow it down
  // to the plain `(key, fallback) => string` shape `buildChips` uses.
  const chips = useMemo(
    () => buildChips(filters, (k, fb) => t(k, { defaultValue: fb }) as string),
    [filters, t],
  );

  if (!hasActiveFilters(filters)) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
      <span className="text-xs font-medium text-muted-foreground mr-1">
        {t('dashboard.filters.activeLabel', 'Filters:')}
      </span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => clearFilter(c.key as keyof DashboardFilters)}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/15 transition-colors"
        >
          <span>{c.label}</span>
          <X className="h-3 w-3" />
        </button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={clearAll}
      >
        {t('dashboard.filters.clearAll', 'Clear all')}
      </Button>
    </div>
  );
}

interface Chip {
  key: keyof DashboardFilters;
  label: string;
}

function buildChips(
  filters: DashboardFilters,
  t: (key: string, fallback?: string) => string,
): Chip[] {
  const out: Chip[] = [];

  if (filters.rejectionCategory) {
    out.push({
      key: 'rejectionCategory',
      label: `${t('dashboard.filters.rejection', 'Rejection')}: ${rejectionLabel(filters.rejectionCategory, t)}`,
    });
  }
  if (filters.device) {
    out.push({
      key: 'device',
      label: `${t('dashboard.filters.device', 'Device')}: ${filters.device}`,
    });
  }
  if (filters.browser) {
    out.push({
      key: 'browser',
      label: `${t('dashboard.filters.browser', 'Browser')}: ${filters.browser}`,
    });
  }
  if (filters.status) {
    out.push({
      key: 'status',
      label: `${t('dashboard.filters.status', 'Status')}: ${t(`dashboard.filters.status_${filters.status}`, filters.status)}`,
    });
  }
  if (filters.dateBucket) {
    out.push({
      key: 'dateBucket',
      label: `${t('dashboard.filters.date', 'Date')}: ${filters.dateBucket}`,
    });
  }
  if (filters.country) {
    out.push({
      key: 'country',
      label: `${t('dashboard.filters.country', 'Country')}: ${filters.country}`,
    });
  }
  if (filters.idType) {
    out.push({
      key: 'idType',
      label: `${t('dashboard.filters.idType', 'Document type')}: ${filters.idType}`,
    });
  }
  if (filters.sdkVersion) {
    // Decode the composite "<PLATFORM>|<version>" for display so the chip
    // reads "SDK: iOS 2.0.0" instead of leaking the wire format.
    const sep = filters.sdkVersion.indexOf('|');
    const platform = sep === -1 ? filters.sdkVersion : filters.sdkVersion.slice(0, sep);
    const version = sep === -1 ? '' : filters.sdkVersion.slice(sep + 1);
    const platformLabel =
      platform === 'IOS' ? 'iOS' :
      platform === 'ANDROID' ? 'Android' :
      platform === 'WEB' ? 'Web' :
      platform === 'FLUTTER' ? 'Flutter' :
      platform;
    out.push({
      key: 'sdkVersion',
      label: `${t('dashboard.filters.sdkVersion', 'SDK')}: ${platformLabel}${version ? ' ' + version : ''}`,
    });
  }
  return out;
}

function rejectionLabel(
  c: RejectionCategory,
  t: (key: string, fallback?: string) => string,
): string {
  const map: Record<RejectionCategory, string> = {
    device_rate_limit: 'deviceRateLimit',
    device_integrity: 'deviceIntegrity',
    liveness_spoofing: 'livenessSpoofing',
    biometric_mismatch: 'biometricMismatch',
    document_invalid: 'documentInvalid',
    other: 'other',
  };
  return t(`dashboard.rejectionReasons.${map[c]}`, c);
}
